import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useGetDetailedUser } from '@/generated/client/users/users';

import { useUserFormOptions } from '../../api/userFormOptionsQueries';
import { getFormValues } from '../../api/userMappers';
import { userAccessRoutes } from '../../utils/userAccessRoutes';
import { UserAccessHeader } from '../user-access-header/UserAccessHeader';
import { UserAccessErrorState } from '../user-access-state-panel/UserAccessErrorState';
import { UserAccessLoadingState } from '../user-access-state-panel/UserAccessLoadingState';
import { EditUserFormContent } from './EditUserFormContent';

type EditUserFormViewProps = {
  userId: number;
  isSelfService: boolean;
};

export function EditUserFormView({ userId, isSelfService }: EditUserFormViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const {
    customers,
    roles,
    isPending: isOptionsPending,
    isError: hasOptionsError,
    retryOptions,
  } = useUserFormOptions();
  const detailedUserQuery = useGetDetailedUser({ userid: userId });
  const detailedUser = detailedUserQuery.data?.data;
  const defaultValues = detailedUser ? getFormValues(detailedUser, customers) : undefined;

  function handleCancel() {
    navigate({ pathname: userAccessRoutes.root, search: location.search }, { state: location.state });
  }

  function handleRetry() {
    void Promise.all([detailedUserQuery.refetch(), retryOptions()]);
  }

  const header = <UserAccessHeader title={t('userAccessControl.editUser')} onBack={handleCancel} />;

  if ((detailedUserQuery.isError && !detailedUser) || hasOptionsError) {
    return <UserAccessErrorState header={header} onRetry={handleRetry} />;
  }

  if (detailedUserQuery.isLoading || isOptionsPending) {
    return <UserAccessLoadingState header={header} />;
  }

  if (!defaultValues || !detailedUser) {
    return <UserAccessErrorState header={header} message={t('userAccessControl.errors.editDataUnavailable')} />;
  }

  return (
    <EditUserFormContent
      userId={userId}
      user={detailedUser}
      initialValues={defaultValues}
      customers={customers}
      roles={roles}
      isSelfService={isSelfService}
      header={header}
      onCancel={handleCancel}
    />
  );
}
